import { useContext } from 'react';
import { createContext } from 'react';
import { TipoModalAlerta } from '../componentes/comuns/ModalAlerta';

/**
 * Opções para exibir um alerta simples
 */
export interface OpcoesAlerta {
  /** Título do modal */
  titulo: string;
  
  /** Mensagem do modal */
  mensagem: string;
  
  /** Callback executado quando usuário clica em "OK" (opcional) */
  aoConfirmar?: () => void;
  
  /** Texto customizado para o botão OK (opcional) */
  textoConfirmar?: string;
}

/**
 * Opções para exibir uma confirmação
 */
export interface OpcoesConfirmacao {
  /** Título do modal */
  titulo: string;
  
  /** Mensagem do modal */
  mensagem: string;
  
  /** Callback executado quando usuário clica em "Confirmar" */
  aoConfirmar: () => void;
  
  /** Callback executado quando usuário clica em "Cancelar" (opcional) */
  aoCancelar?: () => void;
  
  /** Texto customizado para o botão de confirmação (opcional) */
  textoConfirmar?: string;
  
  /** Texto customizado para o botão de cancelamento (opcional) */
  textoCancelar?: string;
}

/**
 * Interface do contexto do modal
 * Define as funções disponíveis para componentes que consomem o contexto
 */
export interface ContextoModal {
  /** Exibe um alerta simples (apenas botão OK) */
  mostrarAlerta: (opcoes: OpcoesAlerta) => void;
  
  /** Exibe uma confirmação (botões OK e Cancelar) */
  mostrarConfirmacao: (opcoes: OpcoesConfirmacao) => void;
  
  /** Fecha o modal atual */
  fecharModal: () => void;
}

/**
 * Estado interno do modal
 * Armazena todas as informações necessárias para renderizar o modal
 */
interface EstadoModal {
  /** Indica se o modal está visível */
  estaVisivel: boolean;
  
  /** Título do modal */
  titulo: string;
  
  /** Mensagem do modal */
  mensagem: string;
  
  /** Tipo do modal: 'alerta' ou 'confirmacao' */
  tipo: TipoModalAlerta;
  
  /** Callback de confirmação */
  aoConfirmar: () => void;
  
  /** Callback de cancelamento */
  aoCancelar?: () => void;
  
  /** Texto customizado do botão de confirmação */
  textoConfirmar?: string;
  
  /** Texto customizado do botão de cancelamento */
  textoCancelar?: string;
}

/**
 * Estado inicial do modal
 * Define valores padrão quando nenhum modal está ativo
 */
export const ESTADO_INICIAL_MODAL: EstadoModal = {
  estaVisivel: false,
  titulo: '',
  mensagem: '',
  tipo: 'alerta',
  aoConfirmar: () => {},
};

/**
 * Cria o contexto do modal
 * Inicializado com undefined para forçar uso do Provider
 */
export const ModalContext = createContext<ContextoModal | undefined>(undefined);

/**
 * Hook customizado para acessar o contexto do modal
 * 
 * IMPLEMENTAÇÃO:
 * - Verifica se o contexto está disponível
 * - Lança erro se usado fora do Provider
 * - Retorna as funções do contexto
 * 
 * WHY: Fornece uma interface conveniente e type-safe para usar o modal
 * 
 * Returns:
 *   Objeto com funções para exibir alertas e confirmações
 * 
 * Throws:
 *   Error se usado fora do ModalProvider
 * 
 * Exemplo de uso:
 * ```tsx
 * function MeuComponente() {
 *   const { mostrarAlerta, mostrarConfirmacao } = useModal();
 *   
 *   const handleClick = () => {
 *     mostrarAlerta({
 *       titulo: 'Sucesso',
 *       mensagem: 'Operação realizada com sucesso!'
 *     });
 *   };
 *   
 *   const handleDelete = () => {
 *     mostrarConfirmacao({
 *       titulo: 'Confirmar Exclusão',
 *       mensagem: 'Tem certeza que deseja excluir este item?',
 *       aoConfirmar: () => {
 *         // Executa a exclusão
 *       }
 *     });
 *   };
 * }
 * ```
 */
export function useModal(): ContextoModal {
  const contexto = useContext(ModalContext);
  if (!contexto) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return contexto;
}

